import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavItemButton } from '@/components/ui/navigation/NavItemButton';
import { NAV_ITEMS, type BadgeState } from '@/lib/navigationItems';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { cn } from "@/lib/utils"
import { useAuthState } from '@/hooks/useAuth';
import { useUserData } from '@/hooks/useUserData';
import { signOutUser } from '@/lib/firebase';
import { AuthModal } from '@/components/auth/AuthModal';
import { cleanupIosOnSignOut } from '@/lib/notifications';
import { Capacitor } from '@capacitor/core';
import { useShareBookingLinkContext } from '@/contexts/ShareBookingLinkContext';

// Define the order of items for the Booking System menu
const BOOKING_SYSTEM_MENU_ITEMS = [
  NAV_ITEMS.BOOKING_SETTINGS,
  NAV_ITEMS.SHARE_BOOKING_LINK,
  NAV_ITEMS.CLIENTS,
  NAV_ITEMS.GET_MORE_TATS,
];

function BookingSystemMenuItems({ onShareLinkClick }: { onShareLinkClick: () => void }) {
  const location = useLocation();

  // Build badge state
  const badgeState: BadgeState = {
    currentPath: location.pathname,
  };

  return (
    <>
      {BOOKING_SYSTEM_MENU_ITEMS.map((item) => (
        <NavItemButton
          key={item.id}
          item={item}
          badgeState={badgeState}
          onNavigate={item.id === 'shareBookingLink' ? onShareLinkClick : undefined}
        />
      ))}
    </>
  );
}

interface BookingSystemMenuProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function BookingSystemMenu({ isOpen, onOpenChange }: BookingSystemMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuthState();
  const { userData, loading: userDataLoading } = useUserData();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { openShareBookingLinkDialog } = useShareBookingLinkContext();

  const handleSignOut = async () => {
    try {
      console.log('[BookingSystemMenu] Starting sign-out...')
      
      // Clean up iOS-specific resources first
      const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
      if (isIOSNative) {
        console.log('[BookingSystemMenu] Cleaning up iOS resources...')
        await cleanupIosOnSignOut()
      }
      
      // Sign out from Firebase
      console.log('[BookingSystemMenu] Signing out from Firebase...')
      await signOutUser();
      
      console.log('[BookingSystemMenu] Sign-out successful')
      setError(null);
      onOpenChange?.(false);
    } catch (error: any) {
      console.error('[BookingSystemMenu] Sign out error:', error);
      console.error('[BookingSystemMenu] Error details:', {
        code: error?.code,
        message: error?.message,
        name: error?.name
      })
      setError(t('auth.signOutError') + ': ' + (error?.message || 'Unknown error'));
    }
  };

  const handleAuthSuccess = () => {
    setError(null);
    onOpenChange?.(false);
  };

  const handleLoginClick = () => {
    onOpenChange?.(false);
    setShowAuthModal(true);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    onOpenChange?.(false);
  };

  const handleShareLinkClick = () => {
    // Close menu first, then open dialog (same pattern as UsersMenu)
    onOpenChange?.(false);
    // Use setTimeout to ensure menu closes before modal opens
    setTimeout(() => {
      openShareBookingLinkDialog();
    }, 0);
  };

  return (
    <>
      <Sheet 
        open={isOpen} 
        onOpenChange={onOpenChange}>
        <SheetContent 
          side="top" 
          hideCloseButton
          className={cn(
            'max-h-[80vh] flex flex-col px-6 pb-6',
            'pt-safe-native-top md:pt-4',
          )}>
          {/* Header with clean auth display */}
          <div className="flex items-center justify-center pb-3 border-b">
            {!authLoading && !userDataLoading && user && userData ? (
              <div className="flex items-center justify-center gap-4">
                <button 
                  onClick={handleProfileClick}
                  className="flex flex-col text-right hover:opacity-80 transition-opacity cursor-pointer"
                >
                  {userData.displayName ? (
                    <>
                      <span className="text-sm font-medium text-foreground">{userData.displayName}</span>
                      <span className="text-xs text-muted-foreground">{userData.email}</span>
                    </>
                  ) : (
                    <>
                      {userData.instagram && (
                        <>
                          <span className="text-sm font-medium text-foreground">{userData.instagram}</span>
                          <span className="text-xs text-muted-foreground">{userData.email}</span>
                        </>
                      )}
                      {!userData.instagram && (
                        <span className="text-base font-medium text-foreground">{userData.email}</span>
                      )}
                    </>
                  )}
                </button>
                <span className="text-muted-foreground">|</span>
                <Button
                  variant="link"
                  className="p-0 h-auto text-base font-medium"
                  onClick={handleSignOut}
                >
                  {t('auth.signOut')}
                </Button>
              </div>
            ) : (
              <Button
                variant="default"
                className="text-base font-medium"
                onClick={handleLoginClick}
              >
                {t('auth.login')}
              </Button>
            )}
          </div>

          {/* Menu Items */}
          <div className="flex flex-col py-4">
            <div className="space-y-1 max-w-xs mx-auto w-full">
              <BookingSystemMenuItems onShareLinkClick={handleShareLinkClick} />

              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-[var(--card-radius)] text-center mt-4">
                  {error}
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AuthModal
        isOpen={showAuthModal}
        onOpenChange={setShowAuthModal}
        onSuccess={handleAuthSuccess}
        source="menu"
        defaultMode="login"
      />
    </>
  );
}
